/**
 * Managed Agents — brand-wide run history (`/client-reports/agents`).
 *
 * One table across every agent type, newest first. The PAGE owns the rows (it
 * polls the first page every 10s while a run is running/generating, merges it
 * over what is loaded, and inserts the optimistic "Run now" row); this
 * component renders them, the plain-language failure banner, the empty-state
 * promise, "Load more", and drives the artifact downloads.
 *
 * DOWNLOADS: only a `ready` run offers download buttons, and only for its
 * `pptx`/`xlsx` artifacts — the HTML half opens through the report-workbench
 * link, and an in-flight (`generating`) row must never show dead buttons.
 * `GET agent-runs/{id}/artifact` answers a short-lived URL which is opened
 * via a throwaway SAME-TAB anchor (no `target`): Safari discards synthetic
 * clicks that would open a new tab after an await, so the click relies on the
 * response's `Content-Disposition: attachment` instead — true for both the
 * signed Spaces URL and the streaming rescue route (authenticated by the
 * httpOnly session cookie). The `emp_` bearer stays inside the API fetch and
 * NEVER appears in any URL.
 */
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import { setMessage } from '../../../store/reducers/appSlice';
import {
  getRunArtifactUrl,
  type AgentArtifactKind,
  type AgentView,
  type RunView,
} from '../../../lib/api/client-reports/client-agents-api';
import {
  agentRunTone,
  downloadableArtifacts,
  findingsChip,
  formatCentsEUR,
  formatRunMoment,
  htmlOutputArtifact,
  isRunInFlight,
  latestRunProblem,
  nextReportPromise,
  runHistoryStatusLabel,
  runPeriodLabel,
  runRowErrorText,
  triggerLabel,
} from './agent-helpers';

interface AgentRunHistoryProps {
  /** All loaded runs for the brand, newest first (page 1 + loaded pages). */
  runs: RunView[];
  /** Activated agents — drive the "first report due …" empty-state promise. */
  agents: AgentView[];
  /** agent_type → human label (registry merged with activated rows). */
  typeLabels: Record<string, string>;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  /**
   * Findings-mode runs (Agents v3) store an HTML scorecard instead of a client
   * report; the host opens it in the sandboxed viewer. Omitted → no button.
   */
  onOpenOutput?: (run: RunView) => void;
}

const AgentRunHistory = ({
  runs,
  agents,
  typeLabels,
  hasMore,
  loadingMore,
  onLoadMore,
  onOpenOutput,
}: AgentRunHistoryProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  /** `${runId}:${kind}` while that artifact's download URL is being fetched. */
  const [preparing, setPreparing] = useState<string | null>(null);

  const problem = latestRunProblem(runs, t);
  const anyActive = agents.some((a) => a.is_active);
  const promise = nextReportPromise(agents, undefined, t);

  const download = async (run: RunView, kind: AgentArtifactKind) => {
    if (preparing) return;
    setPreparing(`${run.id}:${kind}`);
    try {
      const { url } = await getRunArtifactUrl(run.id, kind);
      // Open the short-lived URL through a throwaway SAME-TAB anchor. No
      // `target='_blank'`: Safari treats a post-await synthetic click that
      // opens a new tab as a popup and silently drops it, while a same-tab
      // click rides on the response's `Content-Disposition: attachment`
      // (signed Spaces URL and streaming rescue route alike) without
      // navigating away. And never the bearer in a URL.
      const a = document.createElement('a');
      a.href = url;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err: any) {
      dispatch(
        setMessage({
          message:
            err?.message ||
            t(
              'AgentRunDownloadFailed',
              'Failed to prepare the {{kind}} download.',
              { kind: kind.toUpperCase() }
            ),
          messageType: 'error',
        })
      );
    } finally {
      setPreparing(null);
    }
  };

  return (
    <section className='client-agents__card'>
      <div className='client-agents__card-head'>
        <div className='client-agents__card-id'>
          <h3 className='client-agents__card-title'>
            {t('AgentRunHistoryTitle', 'Run history')}
          </h3>
        </div>
      </div>
      <p className='client-agents__card-hint'>
        {t(
          'AgentRunHistoryHint',
          'Every report this agent produces for this brand, newest first. Finished reports also land in your Action queue for review.'
        )}
      </p>

      {problem && (
        <div className='client-agents__banner' role='alert'>
          <span className='client-agents__banner-title'>{problem.title}</span>
          <span className='client-agents__banner-body'>{problem.body}</span>
        </div>
      )}

      {runs.length === 0 ? (
        <div className='client-agents__table-empty'>
          {anyActive
            ? promise
              ? t('AgentNoReportsYetNext', 'No reports yet — {{next}}.', {
                  next: promise,
                })
              : t(
                  'AgentNoReportsYetSoon',
                  'No reports yet — the first run date appears here shortly.'
                )
            : t(
                'AgentRunHistorySwitchOn',
                'Switch this agent on above and its finished reports land here — and in your report review queue — automatically.'
              )}
        </div>
      ) : (
        <>
          <div className='client-agents__table' role='table'>
            <div
              className='client-agents__trow client-agents__trow--head'
              role='row'
            >
              <span role='columnheader'>
                {t('AgentRunColPeriod', 'Period')}
              </span>
              <span role='columnheader'>
                {t('AgentRunColAgent', 'Agent')}
              </span>
              <span role='columnheader'>
                {t('AgentRunColTrigger', 'Trigger')}
              </span>
              <span role='columnheader'>
                {t('AgentRunColStatus', 'Status')}
              </span>
              <span role='columnheader'>
                {t('AgentRunColGenerated', 'Generated')}
              </span>
              <span role='columnheader'>
                {t('AgentRunColCost', 'Cost')}
              </span>
              <span role='columnheader'>
                {t('AgentRunColDeliverables', 'Deliverables')}
              </span>
            </div>

            {runs.map((r) => {
              const errorText = runRowErrorText(r, t);
              // Download buttons exist only on ready runs and only for the
              // pptx/xlsx kinds — the helper drops everything else.
              const artifacts = downloadableArtifacts(r);
              const htmlOutput = onOpenOutput ? htmlOutputArtifact(r) : null;
              const chip = findingsChip(r, t) ?? {
                label: runHistoryStatusLabel(r.status, t),
                tone: agentRunTone(r.status),
              };
              return (
                <div key={r.id} className='client-agents__trow' role='row'>
                  <span className='client-agents__cell' role='cell'>
                    {runPeriodLabel(r, t)}
                  </span>
                  <span className='client-agents__cell' role='cell'>
                    {typeLabels[r.agent_type] || r.agent_type}
                  </span>
                  <span className='client-agents__cell' role='cell'>
                    {triggerLabel(r.trigger, t)}
                  </span>
                  <span className='client-agents__cell' role='cell'>
                    <span
                      className={`client-agents__chip client-agents__chip--${chip.tone}${
                        isRunInFlight(r.status)
                          ? ' client-agents__chip--pulse'
                          : ''
                      }`}
                    >
                      {chip.label}
                    </span>
                  </span>
                  <span className='client-agents__cell' role='cell'>
                    {formatRunMoment(r.finished_at, undefined, t) || '—'}
                  </span>
                  <span className='client-agents__cell' role='cell'>
                    {formatCentsEUR(r.cost_cents)}
                  </span>
                  <span
                    className='client-agents__cell client-agents__cell--links'
                    role='cell'
                  >
                    {r.client_report_id && (
                      <button
                        type='button'
                        className='client-agents__open-link'
                        onClick={() =>
                          navigate(`/client-reports/${r.client_report_id}`)
                        }
                      >
                        {t('AgentRunOpenReport', 'Open report')}
                      </button>
                    )}
                    {htmlOutput && (
                      <button
                        type='button'
                        className='client-agents__open-link'
                        onClick={() => onOpenOutput?.(r)}
                      >
                        {t('AgentRunOpenOutput', 'Open output')}
                      </button>
                    )}
                    {artifacts.map((art) => (
                      <button
                        key={art.kind}
                        type='button'
                        className='client-agents__rowbtn'
                        disabled={preparing !== null}
                        title={art.filename}
                        onClick={() => download(r, art.kind)}
                      >
                        {preparing === `${r.id}:${art.kind}`
                          ? t('AgentRunPreparing', 'Preparing…')
                          : `${art.kind.toUpperCase()} ↓`}
                      </button>
                    ))}
                    {!r.client_report_id && !htmlOutput && artifacts.length === 0 && '—'}
                  </span>
                  {errorText && (
                    <span className='client-agents__run-error' role='cell'>
                      {errorText}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {hasMore && (
            <div className='client-agents__load-more'>
              <MainSquareButton
                type='button'
                value={
                  loadingMore
                    ? t('AgentRunLoading', 'Loading…')
                    : t('AgentRunLoadMore', 'Load more')
                }
                buttonType='light'
                normal
                loading={loadingMore}
                disabled={loadingMore}
                onClick={onLoadMore}
              />
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default AgentRunHistory;
